// Service pour les notifications du chat (rappels de prière, verset du jour)
import axios from 'axios';

interface PrayerTimes {
  Fajr: string;
  Dhuhr: string;
  Asr: string;
  Maghrib: string;
  Isha: string;
}

class ChatNotificationService {
  private permissionGranted: boolean = false;
  private prayerTimeInterval: NodeJS.Timeout | null = null;
  private dailyVerseInterval: NodeJS.Timeout | null = null;

  // Demander la permission pour les notifications
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Ce navigateur ne supporte pas les notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
      return this.permissionGranted;
    }

    return false;
  }

  // Vérifier si les notifications sont activées
  isEnabled(): boolean {
    return this.permissionGranted || Notification.permission === 'granted';
  }

  // Afficher une notification
  showNotification(title: string, body: string, icon?: string): void {
    if (!this.isEnabled()) {
      return;
    }

    new Notification(title, {
      body,
      icon: icon || '🕌',
      badge: '🕌',
      tag: 'islamic-chat',
    });
  }

  // Obtenir les horaires de prière
  private async getPrayerTimes(city: string = 'Dakar', country: string = 'Senegal'): Promise<PrayerTimes | null> {
    try {
      // Utiliser les coordonnées par défaut de Dakar
      const lat = 14.7167;
      const lng = -17.4677;

      const response = await axios.get(
        `https://api.aladhan.com/v1/timings/${new Date().toISOString().split('T')[0]}`,
        {
          params: {
            latitude: lat,
            longitude: lng,
            method: 2, // ISNA
            school: 0, // Hanafi
          },
        }
      );

      const { timings } = response.data.data;
      return {
        Fajr: timings.Fajr,
        Dhuhr: timings.Dhuhr,
        Asr: timings.Asr,
        Maghrib: timings.Maghrib,
        Isha: timings.Isha || timings.Ishae || 'N/A',
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des horaires:', error);
      return null;
    }
  }

  // Démarrer les rappels de prière (vérifie toutes les heures)
  startPrayerReminders(): void {
    if (this.prayerTimeInterval) {
      return; // Déjà démarré
    }

    this.prayerTimeInterval = setInterval(async () => {
      try {
        const prayerTimes = await this.getPrayerTimes();
        if (!prayerTimes) return;

        const now = new Date();
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

        // Vérifier si c'est l'heure d'une prière (à 5 minutes près)
        const prayers = [
          { name: 'Fajr', time: prayerTimes.Fajr },
          { name: 'Dhuhr', time: prayerTimes.Dhuhr },
          { name: 'Asr', time: prayerTimes.Asr },
          { name: 'Maghrib', time: prayerTimes.Maghrib },
          { name: 'Isha', time: prayerTimes.Isha },
        ];

        prayers.forEach((prayer) => {
          if (this.isTimeNear(currentTime, prayer.time, 5)) {
            this.showNotification(
              `⏰ Heure de la prière : ${prayer.name}`,
              `Il est l'heure de la prière ${prayer.name} (${prayer.time})`
            );
          }
        });
      } catch (error) {
        console.error('Erreur lors de la vérification des horaires de prière:', error);
      }
    }, 60 * 1000); // Vérifier toutes les minutes
  }

  // Obtenir un verset aléatoire
  private async getRandomAyah(): Promise<string> {
    try {
      // Utiliser AlQuran Cloud pour un verset aléatoire
      const randomSurah = Math.floor(Math.random() * 114) + 1;
      const response = await axios.get(
        `https://api.alquran.cloud/v1/surah/${randomSurah}/editions/quran-uthmani,fr.hamidullah`
      );

      if (response.data && response.data.data && response.data.data.ayahs) {
        const ayahs = response.data.data.ayahs;
        if (ayahs.length > 0) {
          const randomAyah = ayahs[Math.floor(Math.random() * ayahs.length)];
          const arabicAyah = randomAyah.text;
          const frenchAyah = response.data.data.edition?.find((e: any) => e.identifier === 'fr.hamidullah')?.ayahs?.find((a: any) => a.number === randomAyah.number)?.text || 'Traduction non disponible';
          
          return `${arabicAyah}\n\n${frenchAyah}`;
        }
      }
      
      // Fallback
      return 'Bismillah ar-Rahman ar-Rahim';
    } catch (error) {
      console.error('Erreur lors de la récupération du verset:', error);
      return 'Bismillah ar-Rahman ar-Rahim';
    }
  }

  // Démarrer le verset du jour (une fois par jour à 8h)
  startDailyVerse(): void {
    if (this.dailyVerseInterval) {
      return; // Déjà démarré
    }

    // Vérifier toutes les heures si c'est 8h
    this.dailyVerseInterval = setInterval(async () => {
      const now = new Date();
      if (now.getHours() === 8 && now.getMinutes() === 0) {
        try {
          const verse = await this.getRandomAyah();
          this.showNotification('📖 Verset du jour', verse.substring(0, 100) + '...');
        } catch (error) {
          console.error('Erreur lors de la récupération du verset du jour:', error);
        }
      }
    }, 60 * 1000); // Vérifier toutes les minutes
  }

  // Arrêter les notifications
  stopAll(): void {
    if (this.prayerTimeInterval) {
      clearInterval(this.prayerTimeInterval);
      this.prayerTimeInterval = null;
    }
    if (this.dailyVerseInterval) {
      clearInterval(this.dailyVerseInterval);
      this.dailyVerseInterval = null;
    }
  }

  // Vérifier si une heure est proche d'une autre (à X minutes près)
  private isTimeNear(current: string, target: string, minutes: number): boolean {
    const [currentH, currentM] = current.split(':').map(Number);
    const [targetH, targetM] = target.split(':').map(Number);

    const currentTotal = currentH * 60 + currentM;
    const targetTotal = targetH * 60 + targetM;

    const diff = Math.abs(currentTotal - targetTotal);
    return diff <= minutes;
  }
}

export const chatNotificationService = new ChatNotificationService();
